import { displayManager, THREE, gsap, Linear } from '@powerplay/common';
import { CameraTypes, CameraStates } from './types';
import { defaults } from './config';
export class CameraManager {
    minimalPositionY;
    cameras;
    actualCameraType = CameraTypes.main;
    cameraState = CameraStates.intro;
    cameraTween;
    cameraTweenZoom;
    currentPosition = new THREE.Vector3();
    raycaster = new THREE.Raycaster();
    currentLookAt = new THREE.Vector3();
    movemementCoef;
    actualIdeals;
    gameplayStateStarted = false;
    tweenSettings;
    changePositionLerpSize;
    changePositionLerpStep;
    isActiveChangeIdeals = false;
    skipping = false;
    newIdeals = {
        offset: new THREE.Vector3(),
        lookAt: new THREE.Vector3()
    };
    idealsBeforeChange = {
        offset: new THREE.Vector3(),
        lookAt: new THREE.Vector3()
    };
    tempObject = new THREE.Object3D();
    outroCamera = { percent: 0 };
    defaultIdeals;
    renderSettings;
    rotationCam = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, Math.PI, 0));
    playerQuatLerp;
    tLookAtCoef;
    constructor() {
        this.renderSettings = {
            far: defaults.far,
            fov: defaults.fov,
            near: defaults.near
        };
    }
    getTweenSettings() {
        return this.tweenSettings;
    }
    createCameras() {
        const ratio = displayManager.ratio;
        const { far, fov, near } = this.renderSettings;
        this.cameras = {
            main: new THREE.PerspectiveCamera(fov, ratio, near, far),
            debug: new THREE.PerspectiveCamera(fov, ratio, near, far)
        };
        console.warn(this.cameras.debug.position);
        this.setDefaultIdeals(20, new THREE.Vector3(0, 2, -2), new THREE.Vector3(0, 1.5, 3), 1, false);
        this.createCamera(CameraTypes.main, new THREE.Vector3(0, 0, 0));
        this.createCamera(CameraTypes.debug, defaults.debugPositionStart);
    }
    isThisCameraState(state) {
        return this.cameraState === state;
    }
    setDefaultIdeals(coefSize, idealOffset, idealLookAt, changeLerp, isStaticMovement) {
        this.defaultIdeals = {
            coefSize,
            idealOffset,
            idealLookAt,
            changeLerp,
            isStaticMovement
        };
        this.setUpIdeals();
    }
    setUpIdeals() {
        this.actualIdeals = {
            coefSize: this.defaultIdeals.coefSize,
            idealOffset: this.defaultIdeals.idealOffset,
            idealLookAt: this.defaultIdeals.idealLookAt,
            changeLerp: this.defaultIdeals.changeLerp,
            isStaticMovement: this.defaultIdeals.isStaticMovement
        };
        this.updateMovementCoef(false);
    }
    changeIdeals(idealOffset = this.defaultIdeals.idealOffset, idealLookAt = this.defaultIdeals.idealLookAt, coefSize = this.defaultIdeals.coefSize, changeLerp = this.defaultIdeals.changeLerp, isStaticMovement = this.defaultIdeals.isStaticMovement) {
        this.actualIdeals.coefSize = coefSize;
        this.actualIdeals.changeLerp = changeLerp;
        this.updateMovementCoef(isStaticMovement);
        this.newIdeals.offset = idealOffset;
        this.newIdeals.lookAt = idealLookAt;
        this.changePositionLerpSize = this.actualIdeals.changeLerp;
        this.changePositionLerpStep = 0;
        this.isActiveChangeIdeals = true;
        this.idealsBeforeChange.offset = this.actualIdeals.idealOffset;
        this.idealsBeforeChange.lookAt = this.actualIdeals.idealLookAt;
    }
    changeBaseRenderSettings(near = this.renderSettings.near, far = this.renderSettings.far, fov = this.renderSettings.fov) {
        this.renderSettings = {
            near,
            far,
            fov
        };
        this.changeRenderSettings();
    }
    changeRenderSettings(near = this.renderSettings.near, far = this.renderSettings.far, fov = this.renderSettings.fov) {
        const camera = this.getMainCamera();
        camera.near = near;
        camera.far = far;
        camera.fov = fov;
        camera.updateProjectionMatrix();
    }
    updateMovementCoef(isStaticMovement) {
        if (isStaticMovement) {
            this.movemementCoef = 0;
            return;
        }
        this.movemementCoef = parseFloat(Math.pow(10, -1 * this.actualIdeals.coefSize).toFixed(this.actualIdeals.coefSize));
    }
    createCamera(cameraIndex, position) {
        this.cameras[cameraIndex].position.copy(position);
        this.cameras[cameraIndex].layers.enable(0);
        this.cameras[cameraIndex].layers.enable(1);
    }
    getCamera(cameraIndex) {
        return this.cameras[cameraIndex];
    }
    setCamera(cameraIndex) {
        this.actualCameraType = cameraIndex;
    }
    getMainCamera() {
        return this.cameras.main;
    }
    getActualRenderCamera() {
        return this.cameras[this.actualCameraType];
    }
    setState(newState) {
        this.cameraState = newState;
        this.setZoom(1);
        this.playerQuatLerp = undefined;
        this.changeRenderSettings();
    }
    setTweenSettingsForStates(settingsAllStates) {
        this.tweenSettings = settingsAllStates;
    }
    setPosition(x, y, z) {
        this.getMainCamera().position.set(x, y, z);
    }
    getPointInBetweenByPercent(pointA, pointB, percentage) {
        const dir = pointB.clone().sub(pointA);
        const len = dir.length();
        return pointA.clone().add(dir.normalize().multiplyScalar(len * percentage));
    }
    moveOutro(playerPosition, playerQuaternion, outroQuaternionLerp) {
        if (this.cameraState !== CameraStates.disciplineOutro)
            return;
        const outroSettings = this.tweenSettings.disciplineOutro;
        if (outroSettings?.startPosition === undefined)
            return;
        const camera = this.getMainCamera();
        if (this.playerQuatLerp === undefined)
            this.playerQuatLerp = playerQuaternion;
        let startPosition = outroSettings.startPosition.clone();
        let endPosition = outroSettings.endPosition.clone();
        if (outroQuaternionLerp) {
            this.playerQuatLerp.slerp(playerQuaternion, 0.5);
            startPosition = startPosition.applyQuaternion(this.playerQuatLerp);
            endPosition = endPosition.applyQuaternion(this.playerQuatLerp);
        }
        const point = this.getPointInBetweenByPercent(startPosition, endPosition, this.outroCamera.percent);
        if (outroSettings.offsetCamera) {
            camera.position.set(playerPosition.x, playerPosition.y, playerPosition.z);
            camera.position.add(point);
        }
        else {
            camera.position.set(point.x, point.y, point.z);
        }
    }
    setTLookAtCoef(tLookAtCoef) {
        this.tLookAtCoef = tLookAtCoef;
    }
    moveDiscipline(playerPosition, playerQuaternion, delta, meshes = [], distanceFromGround = 1.5, quatSettings, upVector = new THREE.Vector3(0, 1, 0)) {
        if (this.cameraState !== CameraStates.discipline)
            return;
        const camera = this.getMainCamera();
        this.moveCameraToNewIdeals();
        let t = 1.0 - Math.pow(this.movemementCoef, delta);
        let tLookAt = t;
        if (this.tLookAtCoef !== undefined) {
            tLookAt = 1.0 - Math.pow(this.tLookAtCoef, delta);
        }
        if (!this.gameplayStateStarted) {
            t = 1;
            tLookAt = 1;
            this.gameplayStateStarted = true;
        }
        const idealLookAt = this.actualIdeals.idealLookAt.clone()
            .applyQuaternion(playerQuaternion)
            .add(playerPosition);
        const idealOffset = this.actualIdeals.idealOffset.clone()
            .applyQuaternion(playerQuaternion)
            .add(playerPosition);
        this.currentPosition.lerp(idealOffset, t);
        this.currentLookAt.lerp(idealLookAt, tLookAt);
        this.checkGroundAndCorrectPosition(meshes, distanceFromGround, upVector);
        camera.position.copy(this.currentPosition);
        if (this.minimalPositionY !== undefined && camera.position.y < this.minimalPositionY) {
            camera.position.y = this.minimalPositionY;
        }
        if (quatSettings) {
            const quat = new THREE.Quaternion()
                .copy(quatSettings.newQuat)
                .multiply(this.rotationCam);
            camera.quaternion.slerp(quat, quatSettings.lerpCoef);
        }
        else {
            camera.lookAt(this.currentLookAt);
        }
    }
    move(playerPosition, playerQuaternion, delta, meshes = [], distanceFromGround = 1.5, onlyLookAtPlayer = false, quatSettings, upVector = new THREE.Vector3(0, 1, 0), outroQuaternionLerp = false, playerPositionOffset = 0.75) {
        this.moveOutro(playerPosition, playerQuaternion, outroQuaternionLerp);
        this.moveDiscipline(playerPosition, playerQuaternion, delta, meshes, distanceFromGround, quatSettings, upVector);
        if (onlyLookAtPlayer) {
            const camera = this.getMainCamera();
            camera.lookAt(playerPosition.x, playerPosition.y + playerPositionOffset, playerPosition.z);
        }
    }
    checkGroundAndCorrectPosition(meshes, distanceFromGround, upVector) {
        if (meshes.length === 0)
            return;
        const distance = 100;
        this.raycaster.set(new THREE.Vector3(this.currentPosition.x, this.currentPosition.y + distance, this.currentPosition.z), upVector);
        const intersects = this.raycaster.intersectObjects(meshes);
        const intersectsDistance = (intersects?.[0]?.distance ?? distance + distanceFromGround) - distance;
        if (intersectsDistance < distanceFromGround) {
            this.currentPosition.y += -intersectsDistance + distanceFromGround;
        }
    }
    moveCameraToNewIdeals() {
        if (!this.isActiveChangeIdeals)
            return;
        this.changePositionLerpStep += this.changePositionLerpSize;
        if (this.changePositionLerpStep >= 1) {
            this.changePositionLerpStep = 1;
            this.isActiveChangeIdeals = false;
        }
        this.actualIdeals.idealLookAt = this.idealsBeforeChange.lookAt
            .clone()
            .lerp(this.newIdeals.lookAt, this.changePositionLerpStep);
        this.actualIdeals.idealOffset = this.idealsBeforeChange.offset
            .clone()
            .lerp(this.newIdeals.offset, this.changePositionLerpStep);
    }
    playTween = (yoyo = false, onEndCallback = undefined) => {
        const tweenSettings = this.tweenSettings[this.cameraState];
        if (!tweenSettings) {
            onEndCallback?.();
            return;
        }
        if (Array.isArray(tweenSettings)) {
            const nextIndex = (tweenSettings.length >= 1) ? 1 : undefined;
            this.playOneTween(tweenSettings[0], yoyo, onEndCallback, nextIndex);
            return;
        }
        const tweenSetting = tweenSettings;
        if (this.cameraState === CameraStates.disciplineOutro) {
            this.playOutroTween(tweenSetting);
            return;
        }
        this.playOneTween(tweenSetting, yoyo, onEndCallback);
    };
    playOutroTween(tweenSettings) {
        this.outroCamera.percent = 0;
        gsap.to(this.outroCamera, {
            duration: tweenSettings.duration,
            percent: 1
        });
        if (tweenSettings.zoom)
            this.setZoom(tweenSettings.zoom);
        if (!tweenSettings.durationZoom)
            return;
        if (this.cameraTweenZoom)
            this.cameraTweenZoom.kill();
        this.cameraTweenZoom = gsap.to(this.getMainCamera(), {
            zoom: tweenSettings.zoomEnd,
            duration: tweenSettings.durationZoom,
            onUpdate: () => {
                this.getMainCamera().updateProjectionMatrix();
            },
            callbackScope: this
        });
    }
    playOneTween(tweenSettings, yoyo = false, onEndCallback = undefined, nextIndex = undefined) {
        if (tweenSettings?.startPosition === undefined)
            return;
        this.setPosition(tweenSettings.startPosition.x, tweenSettings.startPosition.y, tweenSettings.startPosition.z);
        if (tweenSettings.startLookAt)
            this.getMainCamera().lookAt(tweenSettings.startLookAt);
        let startQuaternion;
        if (tweenSettings.startRotation) {
            this.tempObject.rotation.set(tweenSettings.startRotation.x, tweenSettings.startRotation.y, tweenSettings.startRotation.z);
            this.tempObject.rotateX(-Math.PI / 2);
            startQuaternion = this.tempObject.quaternion.clone();
            this.getMainCamera().quaternion.set(startQuaternion.x, startQuaternion.y, startQuaternion.z, startQuaternion.w);
        }
        let endQuaternion;
        if (tweenSettings.endRotation) {
            this.tempObject.rotation.set(tweenSettings.endRotation.x, tweenSettings.endRotation.y, tweenSettings.endRotation.z);
            this.tempObject.rotateX(-Math.PI / 2);
            endQuaternion = this.tempObject.quaternion.clone();
        }
        if (tweenSettings.fov) {
            this.changeRenderSettings(undefined, undefined, tweenSettings.fov - defaults.fovOffset);
        }
        if (tweenSettings.zoom)
            this.setZoom(tweenSettings.zoom);
        if (this.cameraTween)
            this.cameraTween.kill();
        this.cameraTween = gsap.to(this.getMainCamera().position, {
            x: tweenSettings.endPosition.x,
            y: tweenSettings.endPosition.y,
            z: tweenSettings.endPosition.z,
            duration: tweenSettings.duration,
            onUpdate: () => {
                const progress = this.cameraTween?.progress() || 0.00001;
                if (startQuaternion && endQuaternion) {
                    this.getMainCamera().quaternion.slerpQuaternions(startQuaternion, endQuaternion, progress);
                }
                if (tweenSettings.fovEnd && tweenSettings.fov) {
                    const baseFov = tweenSettings.fov - defaults.fovOffset;
                    const endFov = tweenSettings.fovEnd - defaults.fovOffset;
                    this.changeRenderSettings(undefined, undefined, baseFov + ((endFov - baseFov) * progress));
                }
            },
            onComplete: () => {
                if (!this.skipping && nextIndex) {
                    const tweenSettingsInstance = this.tweenSettings[this.cameraState];
                    const nextNextIndex = (tweenSettingsInstance.length >= nextIndex + 1) ?
                        undefined :
                        nextIndex + 1;
                    this.playOneTween(tweenSettingsInstance[nextIndex], yoyo, onEndCallback, nextNextIndex);
                }
                else {
                    onEndCallback?.();
                }
            },
            yoyo,
            repeat: yoyo ? -1 : 0,
            ease: Linear.easeNone
        });
        if (!tweenSettings.durationZoom)
            return;
        if (this.cameraTweenZoom)
            this.cameraTweenZoom.kill();
        this.cameraTweenZoom = gsap.to(this.getMainCamera(), {
            zoom: tweenSettings.zoomEnd,
            duration: tweenSettings.durationZoom,
            onUpdate: () => {
                this.getMainCamera().updateProjectionMatrix();
            },
            callbackScope: this
        });
    }
    skipTween = () => {
        this.skipping = true;
        this.cameraTween?.progress(1);
        this.cameraTween = undefined;
        this.cameraTweenZoom?.progress(1);
        this.cameraTweenZoom = undefined;
        this.skipping = false;
    };
    stopTween = () => {
        this.cameraTween?.kill();
        this.cameraTween = undefined;
        this.cameraTweenZoom?.kill();
        this.cameraTweenZoom = undefined;
    };
    setZoom(zoom) {
        this.getMainCamera().zoom = zoom;
        this.getMainCamera().updateProjectionMatrix();
    }
}
//# sourceMappingURL=CameraManager.js.map